import { useEffect, useRef, useState } from 'react';
import { socket } from '../../context/socket';
import ParticlePool from './utils/ParticlePool';
import {
  getSourceColor,
  getAuraColor,
  getDeviceTraits,
  getActivityFade,
  getClusterKey,
  mapSurgeColor,
  isLowPowerDevice,
  isConstellationForced,
} from './utils/TrafficMappings';

const MAX_PARTICLES = 320;
const SURGE_WINDOW_MS = 3000;
const SURGE_THRESHOLD = 5;

const bgOverlay = 'rgba(5, 8, 18, 0.06)';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function useConstellationEngine(canvasRef) {
  const [guestCount, setGuestCount] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);

  const poolRef = useRef(new ParticlePool(MAX_PARTICLES));
  const sessionMapRef = useRef(new Map()); // sessionId -> particle
  const joinHistoryRef = useRef([]);
  const lastSurgeRef = useRef(0);
  const countRef = useRef(0);
  const lowPowerRef = useRef(false);
  const disableAnimRef = useRef(false);
  const clusterAnchorsRef = useRef(new Map());

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const lowPower = isLowPowerDevice();
    lowPowerRef.current = lowPower;
    const forceEnable = isConstellationForced();

    const disableAnimation =
      prefersReducedMotion ||
      (!forceEnable && lowPower) ||
      !window.requestAnimationFrame ||
      !canvas.getContext ||
      typeof performance === 'undefined';

    disableAnimRef.current = disableAnimation;
    setIsDisabled(disableAnimation);

    const pool = poolRef.current;
    const sessionMap = sessionMapRef.current;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const registerAnchor = (group) => {
      if (!group) return null;
      if (!clusterAnchorsRef.current.has(group)) {
        clusterAnchorsRef.current.set(group, {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
        });
      }
      return clusterAnchorsRef.current.get(group);
    };

    const spawnShootingStar = (guest = {}) => {
      const color = getSourceColor(guest);
      const aura = getAuraColor(guest);
      const traits = getDeviceTraits(guest);
      const group = getClusterKey(guest);
      const startFromLeft = Math.random() > 0.5;
      const startX = startFromLeft
        ? -40
        : canvas.width + 40 * Math.random();
      const startY = Math.random() * canvas.height * 0.7;
      const speed = lowPowerRef.current ? 0.4 : 0.75;
      const vx = (startFromLeft ? 1 : -1) * (speed + Math.random() * 0.4);
      const vy = (Math.random() - 0.5) * 0.6 * speed;

      const particle = pool.acquire({
        type: 'arrival',
        sessionId: guest.sessionId || guest.guestId || `g-${Date.now()}`,
        x: startX,
        y: startY,
        vx,
        vy,
        size: traits.size,
        color,
        aura,
        ttl: 1400 + Math.random() * 800,
        glow: 0.9,
        group,
        activityFade: getActivityFade(guest.activityType),
        strength: clamp(1.2 * traits.trailScale, 0.6, 1.8),
      });

      sessionMap.set(particle.sessionId, particle);
      registerAnchor(group);

      if (!lowPowerRef.current) {
        pool.acquire({
          type: 'ripple',
          x: particle.x,
          y: particle.y,
          ttl: 800,
          size: 12,
          color,
          aura,
          alpha: 0.7,
          glow: 0.3,
        });
      }
    };

    const markRemoval = (sessionId) => {
      const particle = sessionMap.get(sessionId);
      if (!particle) return;
      particle.fadeOut = true;
      particle.ttl = Math.min(particle.ttl, particle.age + 1200);
      particle.glow *= 0.5;
      sessionMap.delete(sessionId);
    };

    const pulseUpdate = (sessionId, partialGuest = {}) => {
      const particle = sessionMap.get(sessionId);
      if (!particle) return;
      particle.glow = clamp(particle.glow + 0.25, 0.6, 1.8);
      particle.alpha = 1;
      particle.vx += (Math.random() - 0.5) * 0.2;
      particle.vy += (Math.random() - 0.5) * 0.2;
      if (partialGuest.activityType) {
        particle.activityFade = getActivityFade(partialGuest.activityType);
      }
      if (!lowPowerRef.current) {
        pool.acquire({
          type: 'ripple',
          x: particle.x,
          y: particle.y,
          ttl: 650,
          size: 18,
          color: getSourceColor(partialGuest) || particle.color,
          aura: particle.aura,
          alpha: 0.5,
          glow: 0.25,
        });
      }
    };

    const spawnSurgePulse = (recentGuests = []) => {
      const color = mapSurgeColor(recentGuests);
      const size = Math.max(canvas.width, canvas.height) * 0.55;
      pool.acquire({
        type: 'surge',
        x: canvas.width * 0.5,
        y: canvas.height * 0.5,
        ttl: 1200,
        size,
        color,
        aura: color,
        glow: 1.1,
        alpha: 0.45,
      });
    };

    const refreshCount = (next) => {
      countRef.current = next;
      setGuestCount(next);
    };

    const handleGuestSnapshot = (payload = {}) => {
      const guests = Object.values(payload.guests || {});
      const count = payload.count ?? guests.length ?? 0;
      refreshCount(count);
      sessionMap.clear();
      pool.clear();

      if (disableAnimRef.current) return;
      const capped = guests.slice(0, Math.min(150, guests.length));
      capped.forEach((g) => spawnShootingStar(g));
    };

    const handleGuestDiff = (payload = {}) => {
      const add = Object.values(payload.add || {});
      const update = Object.values(payload.update || {});
      const remove = payload.remove || [];
      const incomingCount =
        payload.count ??
        clamp(
          countRef.current + add.length - remove.length,
          0,
          Number.MAX_SAFE_INTEGER,
        );

      refreshCount(incomingCount);

      if (!disableAnimRef.current) {
        add.forEach((guest) => spawnShootingStar(guest));
        update.forEach((guest) => {
          const sid = guest.sessionId || guest.guestId;
          if (sid) pulseUpdate(sid, guest);
        });
        remove.forEach((sid) => markRemoval(sid));
      }

      if (add.length) {
        const now = Date.now();
        joinHistoryRef.current = [
          ...joinHistoryRef.current,
          ...add.map((guest) => ({ ts: now, guest })),
        ].filter((item) => now - item.ts <= SURGE_WINDOW_MS);

        if (
          joinHistoryRef.current.length >= SURGE_THRESHOLD &&
          now - lastSurgeRef.current > 900
        ) {
          lastSurgeRef.current = now;
          if (!disableAnimRef.current) {
            spawnSurgePulse(joinHistoryRef.current.map((g) => g.guest));
          }
        }
      }
    };

    socket.on('guest:snapshot', handleGuestSnapshot);
    socket.on('guest:diff', handleGuestDiff);

    let animationId = null;
    let lastTs = performance.now();

    const animate = (ts) => {
      if (disableAnimRef.current) return;

      const delta = clamp(ts - lastTs, 0, 48);
      lastTs = ts;
      const speedScale = delta / 16.7;

      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = bgOverlay;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const particles = pool.getParticles();

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        if (!p.active) continue;

        p.age += delta;
        const life = 1 - p.age / p.ttl;
        if (life <= 0) {
          p.active = false;
          continue;
        }

        const activityFade = p.activityFade || 1;

        if (!lowPowerRef.current && p.group) {
          const anchor = registerAnchor(p.group);
          if (anchor) {
            const pullX = (anchor.x - p.x) * 0.0004 * p.strength;
            const pullY = (anchor.y - p.y) * 0.0004 * p.strength;
            p.vx += pullX;
            p.vy += pullY;
          }
        }

        p.vx *= 0.995;
        p.vy *= 0.995;
        p.x += p.vx * speedScale * 1.8;
        p.y += p.vy * speedScale * 1.8;

        if (p.y < -40 || p.y > canvas.height + 40) {
          p.y = (p.y + canvas.height + 40) % (canvas.height + 40);
        }
        if (p.x < -60 || p.x > canvas.width + 60) {
          p.x = (p.x + canvas.width + 60) % (canvas.width + 60);
        }

        p.history.push({ x: p.x, y: p.y });
        if (p.history.length > 4) p.history.shift();

        const alpha = clamp((p.alpha ?? 1) * life * activityFade, 0, 1);
        if (alpha <= 0.01) {
          p.active = false;
          continue;
        }

        ctx.globalCompositeOperation = 'lighter';

        if (p.type === 'surge') {
          const radius = (p.size || 200) * (1 - life * 0.7);
          const gradient = ctx.createRadialGradient(
            p.x,
            p.y,
            0,
            p.x,
            p.y,
            radius,
          );
          gradient.addColorStop(0, `${p.color}cc`);
          gradient.addColorStop(1, `${p.aura}00`);
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.fill();
          continue;
        }

        if (p.type === 'ripple') {
          ctx.strokeStyle = p.color;
          ctx.globalAlpha = alpha * 0.7;
          ctx.lineWidth = 1.5;
          const radius = (p.size || 12) * (1 - life * 0.3);
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
          continue;
        }

        if (!lowPowerRef.current) {
          ctx.globalAlpha = alpha * 0.6;
          ctx.lineWidth = p.size * 0.8;
          ctx.strokeStyle = p.color;
          const tail = p.history[0] || { x: p.x - p.vx * 8, y: p.y - p.vy * 8 };
          ctx.beginPath();
          ctx.moveTo(tail.x, tail.y);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
        }

        const gradient = ctx.createRadialGradient(
          p.x,
          p.y,
          0,
          p.x,
          p.y,
          14 * (p.size || 1),
        );
        gradient.addColorStop(0, p.color);
        gradient.addColorStop(1, `${p.aura}00`);

        ctx.globalAlpha = alpha * (p.glow || 1);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.4 * (p.size || 1), 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      animationId = requestAnimationFrame(animate);
    };

    if (!disableAnimation) {
      animationId = requestAnimationFrame(animate);
    }

    const cleanup = () => {
      window.removeEventListener('resize', resize);
      socket.off('guest:snapshot', handleGuestSnapshot);
      socket.off('guest:diff', handleGuestDiff);
      if (animationId) cancelAnimationFrame(animationId);
      pool.clear();
      sessionMap.clear();
      clusterAnchorsRef.current.clear();
      joinHistoryRef.current = [];
    };

    return cleanup;
  }, [canvasRef]);

  useEffect(() => {
    // Expose reduced-motion / low-power guard to React tree if needed later.
  }, []);

  return { guestCount, isDisabled };
}
